import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';

const ALTO_LINEA = 1.45;
/** Renglones que se muestran plegado. */
const LINEAS_VISIBLES = 2;
const ALTO_PLEGADO = `${LINEAS_VISIBLES * ALTO_LINEA}em`;
/**
 * El degradé cubre solo el pie del último renglón, no el renglón entero: con
 * un alto de línea completo el texto queda gris ilegible en vez de insinuar
 * que sigue.
 */
const ALTO_DEGRADE = '0.85em';

interface CashNoteProps {
	texto: string;
}

/**
 * La nota de un cobro en efectivo.
 *
 * Hay notas de más de mil caracteres —extractos bancarios pegados enteros— y
 * una tarjeta de Cobros que crece hasta el infinito no la lee nadie. Se
 * muestran dos renglones con el final desvanecido y el resto se despliega a
 * pedido.
 *
 * El "ver más" solo aparece si el texto realmente se corta: se mide el alto
 * real contra el visible en vez de adivinar por cantidad de caracteres, que
 * falla con el ancho variable de la tarjeta (350 px en el sidebar, todo el
 * ancho en mobile).
 */
export function CashNote({ texto }: CashNoteProps): JSX.Element {
	const [abierta, setAbierta] = useState(false);
	const [seCorta, setSeCorta] = useState(false);
	const ref = useRef<HTMLSpanElement>(null);

	const medir = useCallback(() => {
		const el = ref.current;
		// Plegado el scrollHeight delata el texto que no entra; abierta son
		// iguales y la medición no diría nada útil.
		if (el && !abierta) setSeCorta(el.scrollHeight > el.clientHeight + 1);
	}, [abierta]);

	useEffect(() => {
		medir();
		const el = ref.current;
		if (!el || typeof ResizeObserver === 'undefined') return;
		// La tarjeta cambia de ancho entre layouts: sin esto el "ver más"
		// quedaría pegado a la medición del primer render.
		const observer = new ResizeObserver(medir);
		observer.observe(el);
		return () => observer.disconnect();
	}, [medir, texto]);

	return (
		<Box sx={{ mt: 0.25 }}>
			<Box sx={{ position: 'relative' }}>
				<Typography
					ref={ref}
					variant="caption"
					sx={{
						display: 'block',
						whiteSpace: 'pre-line',
						lineHeight: ALTO_LINEA,
						color: 'text.primary',
						maxHeight: abierta ? 'none' : ALTO_PLEGADO,
						overflow: 'hidden',
					}}
				>
					<Box component="span" sx={{ fontWeight: 700 }}>
						Notas:{' '}
					</Box>
					{texto}
				</Typography>

				{!abierta && seCorta && (
					<Box
						aria-hidden
						sx={{
							position: 'absolute',
							bottom: 0,
							left: 0,
							right: 0,
							height: ALTO_DEGRADE,
							background: (theme) => `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
							pointerEvents: 'none',
						}}
					/>
				)}
			</Box>

			{seCorta && (
				<Link
					component="button"
					type="button"
					variant="caption"
					underline="hover"
					onClick={() => setAbierta((v) => !v)}
					sx={{ mt: 0.25, display: 'inline-block' }}
				>
					{abierta ? 'Ver menos' : 'Ver más'}
				</Link>
			)}
		</Box>
	);
}
